import { 
  Chart, 
  BarController, 
  CategoryScale, 
  LinearScale, 
  BarElement, 
  Title, 
  Tooltip, 
  Legend,
  ChartConfiguration
} from 'chart.js';

// Register the components
Chart.register(
  BarController, 
  CategoryScale, 
  LinearScale, 
  BarElement, 
  Title, 
  Tooltip, 
  Legend
);

/**
 * Initialize charts when DOM is loaded
 */
document.addEventListener('DOMContentLoaded', () => {
  initializeCharts();
});

/**
 * Initialize charts after Turbo navigation events
 */
document.addEventListener('turbo:load', () => {
  initializeCharts();
});

/**
 * Theme colors that match the application's color scheme
 */
const themeColors = {
  // Primary theme colors
  theme500: 'rgba(78, 201, 196, 0.5)',  // theme-500 with opacity
  theme600: 'rgba(151, 222, 220, 0.7)',  // theme-600 with opacity
  theme700: 'rgba(54, 168, 163, 0.8)',  // theme-700 with opacity
  theme800: 'rgba(45, 138, 134, 0.9)',  // theme-800 with opacity
  
  // Border colors
  theme500Border: 'rgba(78, 201, 196, 1)',
  theme700Border: 'rgba(54, 168, 163, 1)',
  
  // Secondary colors
  teal500: 'rgba(78, 201, 196, 0.5)',  // teal-500 with opacity
  teal600: 'rgba(151, 222, 220, 0.7)',  // teal-600 with opacity
  teal700: 'rgba(54, 168, 163, 0.8)',  // teal-700 with opacity
  
  // Border colors for secondary
  teal500Border: 'rgba(78, 201, 196, 1)',
  teal700Border: 'rgba(54, 168, 163, 1)',
};

/**
 * Initialize all charts on the page
 */
function initializeCharts() {
  // Initialize organization summary charts
  const orgSummaryCharts = document.querySelectorAll('[data-chart="org-summary"]');
  orgSummaryCharts.forEach(chartElement => {
    if (chartElement instanceof HTMLCanvasElement) {
      createOrganizationSummaryChart(chartElement);
    }
  });
}

/**
 * Create a chart for organization summary data
 */
function createOrganizationSummaryChart(canvasElement: HTMLCanvasElement) {
  // Get data from data attributes
  const tasksReceived = parseInt(canvasElement.dataset.tasksReceived || '0');
  const tasksCompleted = parseInt(canvasElement.dataset.tasksCompleted || '0');
  const hoursWorked = parseFloat(canvasElement.dataset.hoursWorked || '0');
  const hoursExpected = parseFloat(canvasElement.dataset.hoursExpected || '0');

  // Create chart configuration
  const config: ChartConfiguration = {
    type: 'bar',
    data: {
      labels: ['Tasks', 'Hours'],
      datasets: [
        {
          label: 'Received/Expected',
          data: [tasksReceived, hoursExpected],
          backgroundColor: themeColors.theme500,
          borderColor: themeColors.theme500Border,
          borderWidth: 1
        },
        {
          label: 'Completed/Worked',
          data: [tasksCompleted, hoursWorked],
          backgroundColor: themeColors.teal500,
          borderColor: themeColors.teal500Border,
          borderWidth: 1
        }
      ]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top',
          labels: {
            usePointStyle: true,
            padding: 20
          }
        },
        tooltip: {
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          titleColor: '#6B7280', // gray-500
          bodyColor: '#111827', // gray-900
          borderColor: '#E5E7EB', // gray-200
          borderWidth: 1,
          padding: 12,
          boxPadding: 6,
          usePointStyle: true,
          callbacks: {
            labelTextColor: () => '#111827' // gray-900
          }
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          grid: {
            color: 'rgba(243, 244, 246, 1)' // gray-100
          },
          ticks: {
            color: '#6B7280' // gray-500
          }
        },
        x: {
          grid: {
            display: false
          },
          ticks: {
            color: '#6B7280' // gray-500
          }
        }
      }
    }
  };

  // Destroy existing chart if it exists
  const chartId = canvasElement.id;
  const existingChart = Chart.getChart(chartId);
  if (existingChart) {
    existingChart.destroy();
  }

  // Create new chart
  new Chart(canvasElement, config);
} 